/* ==========================================================================
   newsletter-popup.js
   Outerhours Store v2.0 — First-visit newsletter popup.
   Native <dialog> + Klaviyo Client Subscriptions API.
   Mirrors brand-lightbox.js conventions: body-hoist, _initialized guard,
   _onDialogClick backdrop close, ::backdrop tint via CSS.
   ========================================================================== */

if (!customElements.get('newsletter-popup')) {
  class NewsletterPopup extends HTMLElement {
    constructor() {
      super();
      this._isOpen = false;
      this._showTimer = null;

      this._onDialogClick = this._onDialogClick.bind(this);
      this._onDialogClose = this._onDialogClose.bind(this);
      this._onCancel = this._onCancel.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
    }

    connectedCallback() {
      // Guard re-entry: document.body.appendChild() below disconnects then
      // reconnects this element, which fires connectedCallback again.
      if (this._initialized) return;
      this._initialized = true;

      // Move to body so the <dialog> claims the top layer cleanly outside
      // any transformed/scrolled ancestor (mirrors brand-lightbox.js).
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }

      this.dialog = this.querySelector('.newsletter-popup__dialog');
      this.form = this.querySelector('[data-newsletter-popup-form]');
      this.formWrap = this.querySelector('[data-newsletter-popup-form-wrap]');
      this.successEl = this.querySelector('[data-newsletter-popup-success]');
      this.errorEl = this.querySelector('[data-newsletter-popup-error]');
      this.closeBtn = this.querySelector('[data-newsletter-popup-close]');
      this.emailInput = this.querySelector('input[type="email"]');
      this.submitBtn = this.querySelector('.newsletter-popup__submit');

      this.cfg = this._readConfig();

      // Expose a manual trigger for Theme Editor preview / debugging.
      window.OuterhoursNewsletterPopup = {
        open: () => this._open(),
        close: () => this._close('manual'),
        reset: () => {
          try {
            localStorage.removeItem(this.cfg.storageKey);
            sessionStorage.removeItem(this.cfg.storageKey + ':session');
          } catch (_) {}
        }
      };

      this._wire();

      if (!this._shouldShow()) return;
      this._scheduleShow();
    }

    disconnectedCallback() {
      if (this._showTimer) {
        clearTimeout(this._showTimer);
        this._showTimer = null;
      }
    }

    _readConfig() {
      const ds = this.dataset;
      return {
        enabled: ds.enabled !== 'false',
        delaySeconds: Math.max(0, parseInt(ds.delaySeconds, 10) || 0),
        frequency: ds.frequency || 'never_again',
        suppressOnCheckout: ds.suppressOnCheckout !== 'false',
        companyId: ds.klaviyoCompanyId || '',
        listId: ds.klaviyoListId || '',
        source: ds.klaviyoSource || 'newsletter_popup_10off',
        storageKey: ds.storageKey || 'oh:newsletter-popup:v1',
        errorEmail: ds.errorEmail || 'Please enter a valid email address.',
        errorGeneric: ds.errorGeneric || 'Something went wrong. Please try again.'
      };
    }

    _wire() {
      if (this.dialog) {
        this.dialog.addEventListener('click', this._onDialogClick);
        // ESC key fires 'cancel' before the dialog auto-closes — more reliable
        // than 'close' (which doesn't always fire across browsers).
        this.dialog.addEventListener('cancel', this._onCancel);
        // Keep 'close' as a belt-and-suspenders fallback; the handler is
        // idempotent via its internal _isOpen guard.
        this.dialog.addEventListener('close', this._onDialogClose);
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', this._onCloseClick);
      }
      if (this.form) {
        this.form.addEventListener('submit', this._onSubmit);
      }
    }

    _shouldShow() {
      if (!this.cfg.enabled) return false;

      // Never auto-open in the Theme Editor.
      if (
        document.documentElement.classList.contains('shopify-design-mode') ||
        (window.Shopify && window.Shopify.designMode)
      ) {
        return false;
      }

      // Suppress on /cart and /account paths.
      if (this.cfg.suppressOnCheckout) {
        const path = window.location.pathname || '';
        if (path === '/cart' || path.indexOf('/cart/') === 0) return false;
        if (path === '/account' || path.indexOf('/account/') === 0) return false;
      }

      // Frequency / dismissal storage.
      const stored = this._readStorage();
      if (stored && stored.state === 'subscribed') return false;

      if (this.cfg.frequency === 'session') {
        try {
          if (sessionStorage.getItem(this.cfg.storageKey + ':session')) return false;
        } catch (_) {}
      } else if (this.cfg.frequency === 'never_again') {
        if (stored && (stored.state === 'dismissed' || stored.state === 'shown')) return false;
      } else if (this.cfg.frequency === 'dismissed_30') {
        if (stored && stored.state === 'dismissed' && stored.ts) {
          const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
          if (Date.now() - stored.ts < THIRTY_DAYS) return false;
        }
      }

      return true;
    }

    _readStorage() {
      try {
        const raw = localStorage.getItem(this.cfg.storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
      } catch (_) {
        return null;
      }
    }

    _writeStorage(value) {
      try {
        localStorage.setItem(
          this.cfg.storageKey,
          JSON.stringify({ state: value.state, ts: Date.now() })
        );
      } catch (_) {}
      if (this.cfg.frequency === 'session' && value.state !== 'subscribed') {
        try {
          sessionStorage.setItem(this.cfg.storageKey + ':session', '1');
        } catch (_) {}
      }
    }

    _scheduleShow() {
      const delayMs = this.cfg.delaySeconds * 1000;
      this._showTimer = window.setTimeout(() => {
        this._showTimer = null;
        this._open();
      }, delayMs);
    }

    _open() {
      if (this._isOpen || !this.dialog) return;
      this._isOpen = true;

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      document.body.classList.add('overflow-hidden');

      // Move focus to the email input on next tick so the dialog's own
      // focus management settles first.
      requestAnimationFrame(() => {
        if (this.emailInput) this.emailInput.focus();
      });

      this._writeStorage({ state: 'shown' });
      this.dispatchEvent(new CustomEvent('popup:shown', { bubbles: true }));
    }

    _close(reason) {
      if (!this._isOpen || !this.dialog) return;

      // Do all cleanup synchronously up-front. The native 'close' event
      // on <dialog> doesn't always dispatch reliably (seen in field on
      // both Chrome and mobile Safari), so we can't depend on
      // _onDialogClose firing. If it does fire later, its internal guard
      // makes it a no-op.
      this._isOpen = false;
      document.body.classList.remove('overflow-hidden');

      const stored = this._readStorage();
      if (!stored || stored.state !== 'subscribed') {
        this._writeStorage({ state: 'dismissed' });
      }

      try {
        if (typeof this.dialog.close === 'function') {
          this.dialog.close(reason || '');
        } else {
          this.dialog.removeAttribute('open');
        }
      } catch (_) {
        /* dialog already closed */
      }
    }

    _onCancel(event) {
      // Intercept native ESC-key cancel so we route through _close for
      // consistent cleanup (storage flag, body class, _isOpen flip).
      event.preventDefault();
      this._close('cancel');
    }

    _onDialogClick(event) {
      // Native <dialog>: click target === dialog itself when user clicks
      // the backdrop (the dialog's box is the backdrop hit area).
      if (event.target === this.dialog) {
        this._close('backdrop');
      }
    }

    _onCloseClick(event) {
      event.preventDefault();
      this._close('button');
    }

    _onDialogClose() {
      if (!this._isOpen) return;
      this._isOpen = false;
      document.body.classList.remove('overflow-hidden');

      // If the user closed without subscribing, persist a dismissal so
      // frequency rules apply on next visit.
      const stored = this._readStorage();
      if (!stored || stored.state !== 'subscribed') {
        this._writeStorage({ state: 'dismissed' });
      }
    }

    _showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    _hideError() {
      if (!this.errorEl) return;
      this.errorEl.textContent = '';
      this.errorEl.hidden = true;
    }

    _onSubmit(event) {
      event.preventDefault();
      if (!this.emailInput || !this.submitBtn) return;

      const email = (this.emailInput.value || '').trim();
      if (!email || !email.includes('@')) {
        this._showError(this.cfg.errorEmail);
        return;
      }

      this._hideError();
      this.submitBtn.disabled = true;

      fetch(
        'https://a.klaviyo.com/client/subscriptions/?company_id=' + this.cfg.companyId,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-10-15'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email,
                      properties: {
                        $source: this.cfg.source,
                        $consent: ['email']
                      }
                    }
                  }
                }
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: this.cfg.listId
                  }
                }
              }
            }
          })
        }
      )
        .then((response) => {
          if (response.status === 202) {
            this._onSubscribed();
          } else {
            this._showError(this.cfg.errorGeneric);
            this.submitBtn.disabled = false;
          }
        })
        .catch(() => {
          this._showError(this.cfg.errorGeneric);
          this.submitBtn.disabled = false;
        });
    }

    _onSubscribed() {
      this._writeStorage({ state: 'subscribed' });
      if (this.formWrap) this.formWrap.hidden = true;
      if (this.successEl) this.successEl.hidden = false;

      // Auto-close after a short pause so the user can read the message.
      window.setTimeout(() => this._close('subscribed'), 3500);
    }
  }

  customElements.define('newsletter-popup', NewsletterPopup);
}
